// Wird nach dem Login gezeigt: Liste der eigenen Stammbäume und derjenigen, bei
// denen man Helfer ist. Pro Stammbaum: Bearbeiten/Anschauen und ein „⋯“-Menü
// (Helfer einladen, löschen bzw. verlassen). Unten: neuen Stammbaum erstellen.
// UserDashboard.jsx – Übersicht aller Stammbäume eines eingeloggten Benutzers
import React, { useState, useEffect, useCallback } from 'react';
import { ExtensionWizard } from './ExtensionWizard';
import './UserDashboard.css';

const API = 'https://editor.bandeleart.ch';

// Kleines Etikett „Entwurf“ oder „Bestellt“.
function StatusBadge({ status }) {
  if (status === 'erstellt') {
    return <span className="ud-badge ud-badge--draft">Entwurf</span>;
  }
  return <span className="ud-badge ud-badge--ordered">Bestellt</span>;
}

// Warnung, wenn ein Entwurf wegen Inaktivität bald gelöscht wird (Datum vom Server).
function DeleteWarning({ deleteAt }) {
  if (!deleteAt) return null;
  const date = new Date(deleteAt);
  const days = Math.max(0, Math.ceil((date.getTime() - Date.now()) / 86400000));
  const dateStr = date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return (
    <div style={{
      marginTop: 6, padding: '6px 10px', borderRadius: 6, fontSize: 13, lineHeight: 1.4,
      background: '#fff4e5', color: '#8a4b00', border: '1px solid #ffd59e',
    }}>
      ⚠ Seit über 23 Tagen nicht geöffnet. Wird am <strong>{dateStr}</strong> gelöscht
      {days > 0 ? ` (in ${days} ${days === 1 ? 'Tag' : 'Tagen'})` : ''}, wenn du ihn nicht öffnest.
    </div>
  );
}

// ── Helfer verwalten (nur Besitzer) ────────────────────────────────────
// Fenster „Helfer“: Liste der Helfer/Einladungen und Formular zum Einladen.
// Einladen geht in zwei Schritten (Code an den Besitzer, siehe Server /api/user/tree/invite).
function HelpersModal({ tree, token, onClose, onChanged }) {
  const [helpers, setHelpers] = useState(null);
  const [max, setMax] = useState(5);
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok'|'error', text }
  const [codeStep, setCodeStep] = useState(null); // { email, sentTo } – wartet auf den Einmalcode
  const [code, setCode] = useState('');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Helferliste vom Server laden.
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/user/tree/helpers?treeCode=${encodeURIComponent(tree.code)}`, { headers });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ type: 'error', text: d.error || 'Fehler beim Laden.' }); setHelpers([]); return; }
      setHelpers(d.helpers || []);
      setMax(d.max || 5);
    } catch { setMsg({ type: 'error', text: 'Server nicht erreichbar.' }); setHelpers([]); }
  }, [tree.code, token]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Schritt 1: Einmalcode an den Besitzer anfordern
  const requestCode = async (mail) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/user/tree/invite`, { method: 'POST', headers, body: JSON.stringify({ treeCode: tree.code, email: mail }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ type: 'error', text: d.error || 'Einladung fehlgeschlagen.' }); return; }
      if (d.needsCode) {
        setCodeStep({ email: mail, sentTo: d.sentTo });
        setCode('');
        setMsg(null);
      }
    } catch { setMsg({ type: 'error', text: 'Server nicht erreichbar.' }); }
    finally { setBusy(false); }
  };

  // Formular „Einladen“ abgeschickt.
  const invite = async (e) => {
    e.preventDefault();
    setMsg(null);
    const mail = inviteEmail.trim();
    if (!mail.includes('@')) { setMsg({ type: 'error', text: 'Bitte eine gültige E-Mail-Adresse eingeben.' }); return; }
    await requestCode(mail);
  };

  // Schritt 2: Code eingeben → Einladung wird verschickt
  const confirmCode = async (e) => {
    e.preventDefault();
    setMsg(null);
    const c = code.replace(/\s/g, '');
    if (c.length < 6) { setMsg({ type: 'error', text: 'Bitte den 6-stelligen Code aus der E-Mail eingeben.' }); return; }
    setBusy(true);
    try {
      const mail = codeStep.email;
      const res = await fetch(`${API}/api/user/tree/invite`, { method: 'POST', headers, body: JSON.stringify({ treeCode: tree.code, email: mail, code: c }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg({ type: 'error', text: d.error || 'Einladung fehlgeschlagen.' });
        if (d.codeExpired) setCode('');
        return;
      }
      setMsg({ type: 'ok', text: `Einladung an ${mail} verschickt. Sobald sie angenommen ist, kann die Person mithelfen.` });
      setInviteEmail('');
      setCodeStep(null);
      setCode('');
      load();
    } catch { setMsg({ type: 'error', text: 'Server nicht erreichbar.' }); }
    finally { setBusy(false); }
  };

  // Helfer entfernen bzw. offene Einladung zurückziehen.
  const remove = async (h) => {
    const text = h.status === 'accepted'
      ? `${h.email} als Helfer entfernen? Die Person hat danach keinen Zugriff mehr auf den Stammbaum.`
      : `Einladung an ${h.email} zurückziehen?`;
    if (!window.confirm(text)) return;
    try {
      await fetch(`${API}/api/user/tree/helper/remove`, { method: 'POST', headers, body: JSON.stringify({ treeCode: tree.code, email: h.email }) });
    } catch {}
    load();
    onChanged();
  };

  const statusLabel = { accepted: 'hilft mit', pending: 'eingeladen', expired: 'Einladung abgelaufen' };
  const used = (helpers || []).length;

  return (
    <div className="ud-modal-overlay" onClick={onClose}>
      <div className="ud-modal" onClick={e => e.stopPropagation()}>
        <div className="ud-modal-head">
          <h3>Helfer für „{tree.name}“</h3>
          <button className="ud-modal-close" onClick={onClose} aria-label="Schliessen">×</button>
        </div>
        <p className="ud-modal-text">
          Helfer können den Stammbaum mit ihrer eigenen E-Mail-Adresse mitbearbeiten. Löschen, Bestellen und
          weitere Helfer einladen kannst nur du. Es kann immer nur eine Person gleichzeitig bearbeiten.
        </p>

        {helpers === null ? <p className="ud-modal-text">Wird geladen…</p> : (
          <div className="ud-helper-list">
            {helpers.length === 0 && <div className="ud-helper-empty">Noch keine Helfer.</div>}
            {helpers.map(h => (
              <div key={h.email} className="ud-helper-row">
                <div>
                  <div className="ud-helper-mail">{h.email}</div>
                  <div className={`ud-helper-status ud-helper-status--${h.status}`}>{statusLabel[h.status] || h.status}</div>
                </div>
                <button className="ud-link-danger" onClick={() => remove(h)}>
                  {h.status === 'accepted' ? 'Entfernen' : 'Zurückziehen'}
                </button>
              </div>
            ))}
          </div>
        )}

        {helpers !== null && used < max && codeStep && (
          <form onSubmit={confirmCode} className="ud-code-step">
            <p className="ud-modal-text" style={{ margin: '4px 0 8px' }}>
              Zur Sicherheit haben wir dir einen Code an <strong>{codeStep.sentTo}</strong> geschickt.
              Gib ihn hier ein, um <strong>{codeStep.email}</strong> einzuladen.
            </p>
            <div className="ud-invite-form">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-stelliger Code"
                maxLength={7}
                value={code}
                onChange={e => setCode(e.target.value.replace(/[^0-9 ]/g, ''))}
                autoFocus
              />
              <button className="ud-btn ud-btn--primary" type="submit" disabled={busy}>
                {busy ? 'Wird geprüft…' : 'Bestätigen'}
              </button>
            </div>
            <div className="ud-code-links">
              <button type="button" className="ud-link" disabled={busy} onClick={() => requestCode(codeStep.email)}>Neuen Code senden</button>
              <button type="button" className="ud-link" disabled={busy} onClick={() => { setCodeStep(null); setCode(''); setMsg(null); }}>Abbrechen</button>
            </div>
          </form>
        )}
        {helpers !== null && used < max && !codeStep && (
          <form onSubmit={invite} className="ud-invite-form">
            <input
              type="email"
              placeholder="E-Mail-Adresse des Helfers"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              autoFocus
            />
            <button className="ud-btn ud-btn--primary" type="submit" disabled={busy}>
              {busy ? 'Code wird gesendet…' : 'Einladen'}
            </button>
          </form>
        )}
        {helpers !== null && used >= max && (
          <p className="ud-modal-text">Es sind höchstens {max} Helfer (inkl. offener Einladungen) möglich.</p>
        )}
        {msg && <div className={`ud-msg ud-msg--${msg.type}`}>{msg.text}</div>}
        <p className="ud-modal-hint">
          Zur Sicherheit bestätigst du jede Einladung mit einem Code, den du per E-Mail erhältst.
          Die eingeladene Person erhält danach eine E-Mail und muss die Einladung annehmen. Erst dann ist sie mit dem
          Stammbaum verbunden. Einladungen sind 7 Tage gültig.
        </p>
      </div>
    </div>
  );
}

// Props: email/token des eingeloggten Kunden, onSelectTree (Stammbaum öffnen),
// onLogout, onCreateNew (neuen Stammbaum erstellen).
// saveTreeData(treeCode, data) speichert die Daten einer neuen Erweiterung (kommt aus App.jsx).
export function UserDashboard({ email, token, onSelectTree, onLogout, onCreateNew, saveTreeData }) {
  const [trees, setTrees] = useState([]);
  const [showExtension, setShowExtension] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Liste der Stammbäume vom Server holen. Bei 401 (Login abgelaufen) → abmelden.
  const loadTrees = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/user/trees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Fehler beim Laden.'); return; }
      setTrees(data.trees || []);
    } catch {
      setError('Server nicht erreichbar.');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { loadTrees(); }, [loadTrees]);

  // ── „⋯“-Menü pro Stammbaum ──────────────────────────────────────────
  const [menu, setMenu] = useState(null);        // { tree, top, right }
  const [helpersFor, setHelpersFor] = useState(null);
  const authJson = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // „⋯“-Menü direkt unter dem angeklickten Knopf öffnen.
  const openMenu = (e, tree) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenu({ tree, top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  };

  const deleteTree = async (tree) => {
    setMenu(null);
    if (!window.confirm(`Stammbaum „${tree.name}“ komplett löschen?\n\nAlle Personen und Fotos werden endgültig gelöscht. Das kann nicht rückgängig gemacht werden.`)) return;
    try {
      const res = await fetch(`${API}/api/tree/delete`, { method: 'POST', headers: authJson, body: JSON.stringify({ treeCode: tree.code }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { alert(d.error || 'Der Stammbaum konnte nicht gelöscht werden.'); return; }
    } catch { alert('Server nicht erreichbar.'); return; }
    loadTrees();
  };

  // Helfer verlässt einen Stammbaum.
  const leaveTree = async (tree) => {
    setMenu(null);
    if (!window.confirm(`Stammbaum „${tree.name}“ verlassen?\n\nDu hast danach keinen Zugriff mehr. ${tree.ownerEmail || 'Der Besitzer'} kann dich erneut einladen.`)) return;
    try {
      await fetch(`${API}/api/user/tree/helper/remove`, { method: 'POST', headers: authJson, body: JSON.stringify({ treeCode: tree.code, email }) });
    } catch {}
    loadTrees();
  };

  // Bestellte Stammbäume werden nur zum Anschauen geöffnet.
  const handleSelect = (tree) => {
    const readOnly = tree.status !== 'erstellt';
    onSelectTree(tree, readOnly);
  };

  return (
    <div className="ud-overlay">
      <div className="ud-card">
        <div className="ud-header">
          <div>
            <div className="ud-logo">Stammbaum-Memo</div>
            <div className="ud-email">{email}</div>
          </div>
          <button className="ud-logout" onClick={onLogout} title="Abmelden">
            Abmelden
          </button>
        </div>

        <div className="ud-body">
          {loading && (
            <div className="ud-loading">Stammbäume werden geladen…</div>
          )}

          {!loading && error && (
            <div className="ud-error">
              <p>{error}</p>
              <button className="ud-btn ud-btn--ghost" onClick={loadTrees}>Erneut versuchen</button>
            </div>
          )}

          {!loading && !error && trees.length === 0 && (
            <div className="ud-empty">
              <p>Du hast noch keine Stammbäume.</p>
              <button className="ud-btn ud-btn--primary" onClick={onCreateNew} style={{ marginTop: 12 }}>
                Ersten Stammbaum erstellen
              </button>
            </div>
          )}

          {!loading && !error && trees.length > 0 && (
            <div className="ud-list">
              {trees.map(tree => (
                <div key={tree.code} className="ud-tree-row">
                  <div className="ud-tree-info">
                    <div className="ud-tree-name">{tree.name || '(Kein Name)'}</div>
                    <div className="ud-tree-meta">
                      <StatusBadge status={tree.status} />
                      <span className="ud-tree-code">{tree.code}</span>
                      {tree.role === 'owner' && tree.helperCount > 0 && (
                        <span className="ud-tree-shared">{tree.helperCount} {tree.helperCount === 1 ? 'Helfer' : 'Helfer'}</span>
                      )}
                    </div>
                    {tree.role === 'helper' && (
                      <div className="ud-tree-owner">Du hilfst mit · Besitzer: {tree.ownerEmail}</div>
                    )}
                    <DeleteWarning deleteAt={tree.deleteAt} />
                  </div>
                  <div className="ud-tree-actions">
                    <button
                      className={`ud-btn ${tree.status === 'erstellt' ? 'ud-btn--primary' : 'ud-btn--ghost'}`}
                      onClick={() => handleSelect(tree)}
                    >
                      {tree.status === 'erstellt' ? 'Bearbeiten' : 'Anschauen'}
                    </button>
                    <button className="ud-more-btn" onClick={(e) => openMenu(e, tree)} title="Weitere Aktionen" aria-label="Weitere Aktionen">⋯</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {menu && (
          <>
            <div className="ud-menu-backdrop" onClick={() => setMenu(null)} />
            <div className="ud-menu" style={{ top: menu.top, right: menu.right }}>
              {menu.tree.role === 'owner' ? (
                <>
                  <button onClick={() => { setHelpersFor(menu.tree); setMenu(null); }}>Helfer einladen</button>
                  {menu.tree.status === 'erstellt'
                    ? <button className="ud-menu-danger" onClick={() => deleteTree(menu.tree)}>Stammbaum löschen</button>
                    : <div className="ud-menu-note">Bestellte Stammbäume können nicht gelöscht werden.</div>}
                </>
              ) : (
                <button className="ud-menu-danger" onClick={() => leaveTree(menu.tree)}>Stammbaum verlassen</button>
              )}
            </div>
          </>
        )}
        {showExtension && (
          <ExtensionWizard
            trees={trees}
            token={token}
            saveTreeData={saveTreeData}
            onCreated={(tree) => { setShowExtension(false); onSelectTree(tree, false); }}
            onClose={() => setShowExtension(false)}
          />
        )}
        {helpersFor && (
          <HelpersModal tree={helpersFor} token={token} onClose={() => { setHelpersFor(null); loadTrees(); }} onChanged={() => {}} />
        )}

        <div className="ud-footer">
          <button className="ud-btn ud-btn--new" onClick={onCreateNew}>
            + Neuen Stammbaum erstellen
          </button>
          {trees.length > 0 && saveTreeData && (
            <button className="ud-btn ud-btn--new" style={{ marginTop: 8 }} onClick={() => setShowExtension(true)}>
              + Erweiterung zu einem Stammbaum erstellen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
